import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import FormikInput from "components/formikInput";
import FileUpload from "components/FileUpload";
import { ModuleContent } from "slices/courseSlice";

// interface Content extends ModuleContent {
//   type: "video" | "image_audio";
// }

interface ContentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<ModuleContent>) => void;
  content: ModuleContent | null | undefined;
  languages?: string[];
}

interface LanguageMediaEntry {
  lang: string;
  url: string;
}

interface ContentDialogValues {
  title: string;
  type: "video" | "image_audio";
  videoURL: LanguageMediaEntry[];
  imageURL: string;
  audioURL: string;
  imageURLByLanguage: LanguageMediaEntry[];
  audioURLByLanguage: LanguageMediaEntry[];
  duration: number;
}

export function ContentDialog({ open, onClose, onSave, content, languages }: ContentDialogProps): React.JSX.Element {
  const [contentType, setContentType] = useState<"video" | "image_audio">(content?.type || "video");

  const mapLanguageEntries = (fallbackValue?: ModuleContent["imageURL"]): LanguageMediaEntry[] => {
    if (Array.isArray(fallbackValue)) {
      if (languages && languages.length > 0) {
        return languages.map((lang) => {
          const matching = fallbackValue.find((entry) =>
            (entry.lang || "en").toLowerCase() === lang.toLowerCase()
          );
          return { lang, url: matching?.url || "" };
        });
      }

      return fallbackValue.map((entry) => ({
        lang: entry.lang || "en",
        url: entry.url || "",
      }));
    }

    const fallbackUrl = typeof fallbackValue === "string" ? fallbackValue : "";
    if (languages && languages.length > 0) {
      return languages.map((lang, index) => ({
        lang,
        url: index === 0 ? fallbackUrl || "" : "",
      }));
    }

    return [{ lang: "en", url: fallbackUrl || "" }];
  };

  useEffect(() => {
    if (!open) return;
    setContentType(content?.type || "video");
  }, [content, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{content ? "Edit Content" : "Add Content to Module"}</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            title: content?.title || "",
            type: content?.type || "video",
            videoURL: content?.videoURL || (languages ? languages.map((lang) => ({ lang, url: "" })) : []),
            imageURL: typeof content?.imageURL === "string" ? content.imageURL : content?.imageURL?.[0]?.url || "",
            audioURL: typeof content?.audioURL === "string" ? content.audioURL : content?.audioURL?.[0]?.url || "",
            imageURLByLanguage: mapLanguageEntries(content?.imageURL),
            audioURLByLanguage: mapLanguageEntries(content?.audioURL),
            duration: content?.durationSeconds ?? content?.duration ?? 0,
          } as ContentDialogValues}
          validationSchema={Yup.object({
            title: Yup.string().required("Content title is required"),
            type: Yup.string().required("Content type is required"),
            duration: Yup.number()
              .typeError("Duration must be a valid number")
              .integer("Duration must be a whole number")
              .min(0, "Duration cannot be negative")
              .required("Duration is required"),
          })}
          onSubmit={(values) => {
            if (values.type === "image_audio") {
              const imageByLanguage = values.imageURLByLanguage
                .filter((entry) => entry?.url?.trim())
                .map((entry) => ({ lang: entry.lang || "en", url: entry.url.trim() }));

              const audioByLanguage = values.audioURLByLanguage
                .filter((entry) => entry?.url?.trim())
                .map((entry) => ({ lang: entry.lang || "en", url: entry.url.trim() }));

              onSave({
                ...(values as Partial<ModuleContent>),
                imageURL: imageByLanguage,
                audioURL: audioByLanguage,
              });
            } else {
              onSave(values as Partial<ModuleContent>);
            }
            onClose();
          }}
          enableReinitialize
        >
          {({ submitForm, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormikInput
                    name="title"
                    label="Content Title *"
                    placeholder="e.g., React Components Explained"
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Give this content item a clear title so learners know what they are opening.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormikInput
                    name="type"
                    type="dropdown"
                    label="Content Type *"
                    options={[
                      { value: "video", label: "Video (MP4, WebM, etc.)" },
                      { value: "image_audio", label: "Image + Audio (Image with auto-play audio)" },
                    ]}
                    size="large"
                    onChange={(e: any) => {
                      setContentType(e.target.value);
                      setFieldValue("type", e.target.value);
                    }}
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    Choose whether this lesson is a video or an image with audio narration.
                  </Typography>
                </Grid>
                
                {contentType === "video" ? (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" mb={1} fontWeight="medium">
                      Video File *
                    </Typography>
                    {(languages && languages.length >= 1) ? (
                      languages.map((lang, index) => (
                        <Box key={lang} mb={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FormikInput
                              name={`videoURL[${index}].url`}
                              label="Or Enter Video URL"
                              placeholder="https://example.com/video.mp4"
                              disabled
                              inputProps={{ readOnly: true }}
                              size="medium"
                            />
                            <FileUpload
                              type="video"
                              onUploadComplete={(url, metadata) => {
                                if (typeof url === "string") {
                                  setFieldValue("videoURL", values.videoURL?.map((v) => {
                                    if (v.lang === lang) {
                                      return { lang: v.lang, url };
                                    }
                                    return v;
                                  }) || []);

                                  if (typeof metadata?.durationSeconds === "number") {
                                    setFieldValue("duration", metadata.durationSeconds);
                                  }
                                }
                              }}
                              existingUrl={values.videoURL?.find((v) => v.lang === lang)?.url || ""}
                            />
                            <Typography variant="subtitle2" mb={1} fontWeight="medium" width={'120px'}>
                              [{lang}]
                            </Typography>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box mb={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <FileUpload
                            type="video"
                            label="Upload Video File (MP4, WebM, OGG)"
                            onUploadComplete={(url, metadata) => {
                              if (typeof url === "string") {
                                const lang = languages?.[0] || "en";
                                setFieldValue("videoURL", [{ lang, url }]);

                                if (typeof metadata?.durationSeconds === "number") {
                                  setFieldValue("duration", metadata.durationSeconds);
                                }
                              }
                            }}
                            existingUrl={
                              Array.isArray(values.videoURL) && values.videoURL.length > 0
                                ? values.videoURL[0]?.url || ""
                                : ""
                            }
                          />
                        </Box>
                        <Box mt={2}>
                          <FormikInput
                            name="videoURL[0].url"
                            label="Or Enter Video URL"
                            placeholder="https://example.com/video.mp4"
                            disabled
                            inputProps={{ readOnly: true }}
                            size="large"
                          />
                        </Box>
                      </Box>
                    )}

                    <Typography variant="caption" color="text" mt={0.5} display="block">
                      Upload a video file or enter a video URL.
                    </Typography>
                  </Grid>
                ) : (
                  <>
                    {(languages && languages.length > 0) ? (
                      languages.map((lang, index) => (
                        <Grid item xs={12} key={`image-audio-${lang}`}>
                          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Typography variant="subtitle2" mb={2} fontWeight="medium">
                              [{lang}] Image + Audio
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" mb={1} fontWeight="medium">
                                  Image File *
                                </Typography>
                                <FileUpload
                                  type="image"
                                  label="Upload Image File"
                                  onUploadComplete={(url) => {
                                    if (typeof url === "string") {
                                      setFieldValue(
                                        "imageURLByLanguage",
                                        values.imageURLByLanguage?.map((entry, entryIndex) => (
                                          entryIndex === index ? { ...entry, url } : entry
                                        )) || []
                                      );
                                    }
                                  }}
                                  existingUrl={values.imageURLByLanguage?.[index]?.url || ""}
                                />
                                <Box mt={1}>
                                  <FormikInput
                                    name={`imageURLByLanguage[${index}].url`}
                                    label="Or Enter Image URL"
                                    placeholder="https://example.com/image.jpg"
                                    disabled
                                    inputProps={{ readOnly: true }}
                                    size="medium"
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" mb={1} fontWeight="medium">
                                  Audio File *
                                </Typography>
                                <FileUpload
                                  type="audio"
                                  label="Upload Audio File"
                                  onUploadComplete={(url, metadata) => {
                                    if (typeof url === "string") {
                                      setFieldValue(
                                        "audioURLByLanguage",
                                        values.audioURLByLanguage?.map((entry, entryIndex) => (
                                          entryIndex === index ? { ...entry, url } : entry
                                        )) || []
                                      );

                                      if (typeof metadata?.durationSeconds === "number") {
                                        setFieldValue("duration", metadata.durationSeconds);
                                      }
                                    }
                                  }}
                                  existingUrl={values.audioURLByLanguage?.[index]?.url || ""}
                                />
                                <Box mt={1}>
                                  <FormikInput
                                    name={`audioURLByLanguage[${index}].url`}
                                    label="Or Enter Audio URL"
                                    placeholder="https://example.com/audio.mp3"
                                    disabled
                                    inputProps={{ readOnly: true }}
                                    size="medium"
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      ))
                    ) : (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" mb={1} fontWeight="medium">
                            Image File *
                          </Typography>
                          <FileUpload
                            type="image"
                            label="Upload Image File (JPEG, PNG, GIF)"
                            onUploadComplete={(url) => {
                              if (typeof url === "string") {
                                setFieldValue("imageURL", url);
                              }
                            }}
                            existingUrl={values.imageURL}
                          />
                          <Box mt={1}>
                            <FormikInput
                              name="imageURL"
                              label="Or Enter Image URL"
                              placeholder="https://example.com/image.jpg"
                              disabled
                              inputProps={{ readOnly: true }}
                              size="large"
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" mb={1} fontWeight="medium">
                            Audio File *
                          </Typography>
                          <FileUpload
                            type="audio"
                            label="Upload Audio File (MP3, WAV, OGG, WebM)"
                            onUploadComplete={(url, metadata) => {
                              if (typeof url === "string") {
                                setFieldValue("audioURL", url);

                                if (typeof metadata?.durationSeconds === "number") {
                                  setFieldValue("duration", metadata.durationSeconds);
                                }
                              }
                            }}
                            existingUrl={values.audioURL}
                          />
                          <Box mt={1}>
                            <FormikInput
                              name="audioURL"
                              label="Or Enter Audio URL"
                              placeholder="https://example.com/audio.mp3"
                              disabled
                              inputProps={{ readOnly: true }}
                              size="large"
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <FormikInput
                    name="duration"
                    type="number"
                    label="Content Duration (seconds)"
                    placeholder="0"
                    inputProps={{ min: 0, step: 1 }}
                    disabled
                    size="large"
                  />
                  <Typography variant="caption" color="text" mt={0.5} display="block">
                    This is auto-filled from uploaded video/audio metadata and stored in seconds.
                  </Typography>
                </Grid>
              </Grid>
              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={submitForm} variant="gradient" color="info">
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

